import { ClientLoggingService } from '../../../../../shared/client-logging.service';
import { Component, OnInit, ViewChild, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PlanReviewerService } from '../../../../../shared/plan-reviewer.service';
import { ArchitecturalPlanPage } from '../../../../../shared/models/architectural-plan-page.model';
import { TaskCounterService } from '../../../../../shared/task-counter.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { getTaskUrl } from '../../../../../shared/task-navigation.util';
import { ComplianceSection } from '../compliance.component';
import { ConfirmDialogComponent } from '../../../../../shared/confirm-dialog/confirm-dialog.component';
import { firstValueFrom } from 'rxjs';
import { BillingService } from '../../../../../shared/billing.service';
import { showBillingAwareErrorSnackbar } from '../../../../../shared/billing-error-utils';

export interface CodeApplicabilityDialogData {
  activePlanPage: ArchitecturalPlanPage;
  existingSections?: ComplianceSection[];
}

/** Dialog for selecting additional building codes and chapters to run code-applicability analysis against the current plan page. */
@Component({
  selector: 'app-code-applicability-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatStepperModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './code-applicability-dialog.component.html',
  styleUrls: ['./code-applicability-dialog.component.scss'],
})
export class CodeApplicabilityDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CodeApplicabilityDialogComponent>>(MatDialogRef);
  data = inject<CodeApplicabilityDialogData>(MAT_DIALOG_DATA);
  private planReviewerService = inject(PlanReviewerService);
  private taskCounterService = inject(TaskCounterService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private _formBuilder = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private billingService = inject(BillingService);

  @ViewChild('stepper') stepper!: MatStepper;

  bookStepControl: FormGroup;

  // Book selection properties
  availableBooks: Array<{ id: string; title: string; abbreviation: string; year: string }> = [];
  loadingBooks = false;
  bookLoadError = '';

  // Chapter selection properties
  availableChapters: Array<{ contentId: string; title: string }> = [];
  selectedChapterIds: string[] = [];
  chapterSelectionMode: 'manual' | 'ai' = 'ai';
  maxChapters = 5;
  loadingChapters = false;
  chapterLoadError = '';

  // Section depth properties
  maxSectionDepth = 2; // Default: Sections + Subsections

  // Issue #1426: Force recompute option
  forceRecompute = false;

  depthOptions = [
    { value: 1, label: 'Main sections only (Level 1) - e.g., Section 1104' },
    { value: 2, label: 'Sections + Subsections (Level 1-2) - e.g., 1104.1' },
    { value: 3, label: 'Sections + Subsections + Sub-subsections (Level 1-3) - e.g., 1104.1.1' },
    { value: -1, label: 'All section depths (Level 1+) - all nested subsections' },
  ];

  constructor() {
    this.bookStepControl = this._formBuilder.group({
      selectedBookId: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadAvailableBooks();
  }

  get selectedBookId(): string {
    return this.bookStepControl.get('selectedBookId')?.value || '';
  }

  async loadAvailableBooks(): Promise<void> {
    if (this.loadingBooks) return;

    this.loadingBooks = true;
    this.bookLoadError = '';

    try {
      // Use the service method that priorities page/file context if available,
      // but here we want generally available books for manual selection
      this.availableBooks = await this.planReviewerService.getAllAvailableBooks();

      if (this.availableBooks.length === 0) {
        this.bookLoadError = 'No ICC books are currently available.';
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[CodeApplicabilityDialog] Failed to load available books:', error);
      this.bookLoadError = 'Failed to load available books. Please try again.';
    } finally {
      this.loadingBooks = false;
    }
  }

  async loadChapters(): Promise<void> {
    const bookId = this.selectedBookId;
    if (!bookId) return;

    if (this.loadingChapters) return;

    this.loadingChapters = true;
    this.chapterLoadError = '';

    try {
      this.availableChapters = await this.planReviewerService.getIccBookChapters(bookId);

      if (this.availableChapters.length === 0) {
        this.chapterLoadError = 'No chapters found for this book.';
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[CodeApplicabilityDialog] Failed to load chapters:', error);
      this.chapterLoadError = 'Failed to load chapters. Please try again.';
    } finally {
      this.loadingChapters = false;
    }
  }

  onBookSelected(): void {
    // Clear previous chapter selections when book changes to avoid stale IDs
    this.selectedChapterIds = [];
    this.availableChapters = [];

    // Only load chapters if manual selection mode is chosen
    if (this.chapterSelectionMode === 'manual') {
      this.loadChapters();
    }
  }

  onSelectionModeChange(): void {
    if (this.chapterSelectionMode === 'manual' && this.selectedBookId && this.availableChapters.length === 0) {
      this.loadChapters();
    }
    if (this.chapterSelectionMode === 'ai') {
      this.selectedChapterIds = [];
    }
  }

  removeChapter(chapterId: string): void {
    this.selectedChapterIds = this.selectedChapterIds.filter(id => id !== chapterId);
  }

  getChapterTitle(chapterId: string): string {
    const chapter = this.availableChapters.find(c => c.contentId === chapterId);
    return chapter ? chapter.title : chapterId;
  }

  // Check if a chapter is already present in the existing analysis
  isChapterAnalyzed(chapterId: string): boolean {
    if (!this.data.existingSections) return false;

    // Check if any existing section belongs to this chapter and book
    return this.data.existingSections.some(s => {
      // Check book match (approximation by abbreviation/slug if strict ID not available on section)
      // Note: section.codeBook should match selectedBookId ideally
      // For now, simpler check: if we are in the same book context (implied), check hierarchy
      // Hierarchy [Book, Chapter, ...]
      if (s.hierarchy && s.hierarchy.length > 1) {
        // Assuming hierarchy[1] is the chapter
        // We need to match chapterId (contentId) or chapterSlug handling
        // If chapterId is a slug (e.g. 'chapter-11') and hierarchy gives slug/id
        return s.hierarchy[1].id === chapterId || s.hierarchy[1].chapterSlug === chapterId;
      }
      return false;
    });
  }

  hasOverlap(): boolean {
    return this.selectedChapterIds.some(id => this.isChapterAnalyzed(id));
  }

  isStep2Valid(): boolean {
    if (this.chapterSelectionMode === 'ai') {
      return true;
    } else {
      return !this.loadingChapters && !this.chapterLoadError && this.selectedChapterIds.length > 0;
    }
  }

  overlappingChapters: string[] = [];

  // ... (previous methods)

  checkForOverlap(): void {
    this.overlappingChapters = this.selectedChapterIds.filter(id => this.isChapterAnalyzed(id));
  }

  async onStartAnalysis(): Promise<void> {
    if (!this.data.activePlanPage) return;

    // Check for overlaps before proceeding
    this.checkForOverlap();

    if (this.forceRecompute) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Force Recompute Analysis?',
          message:
            'Forcing recomputation skips cached results and invokes AI models for fresh analysis. This may take a few minutes and consume AI processing credits. Do you want to proceed?',
          confirmText: 'Force Recompute',
          cancelText: 'Cancel',
          confirmColor: 'primary',
        },
        width: '420px',
      });

      const confirmed = await firstValueFrom(dialogRef.afterClosed());
      if (!confirmed) {
        return;
      }
    } else if (this.overlappingChapters.length > 0) {
      // Show Material Design confirmation dialog instead of native confirm()
      const chapterTitles = this.overlappingChapters.map(id => this.getChapterTitle(id)).join(', ');

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Re-analyze Chapters?',
          message: `The following chapters have already been analyzed: ${chapterTitles}. Do you want to re-analyze them?`,
          confirmText: 'Re-analyze',
          cancelText: 'Cancel',
          confirmColor: 'primary',
        },
        width: '400px',
      });

      const confirmed = await firstValueFrom(dialogRef.afterClosed());
      if (!confirmed) {
        return;
      }
    }

    // Proactive balance check before making server call (Issue #553)
    if (await this.billingService.guardInsufficientBalance(this.snackBar, this.router, 'code-applicability')) {
      return;
    }

    try {
      const result = await this.planReviewerService.startCodeApplicabilityAnalysis(
        this.data.activePlanPage.getArchitecturalPlanId(),
        this.data.activePlanPage.getPageNumber(),
        this.selectedBookId,
        this.chapterSelectionMode,
        this.maxChapters,
        this.selectedChapterIds,
        this.maxSectionDepth,
        this.forceRecompute
      );

      if (result.success) {
        // Add task to TaskCounterService for tracking
        const taskDisplayName = `Code applicability analysis - Page ${this.data.activePlanPage.getPageNumber()}`;
        this.taskCounterService.addTask(
          result.taskId,
          this.data.activePlanPage.getArchitecturalPlanId(),
          taskDisplayName
        );

        // Show success snackbar with task ID
        const planId = this.data.activePlanPage!.getArchitecturalPlanId();
        const snackBarRef = this.snackBar.open(`Analysis started. Task ID: ${result.taskId}`, 'View Task', {
          duration: 5000,
        });

        snackBarRef.onAction().subscribe(() => {
          this.router.navigate(getTaskUrl(planId, result.taskId));
        });

        this.dialogRef.close(true);
      } else {
        showBillingAwareErrorSnackbar(this.snackBar, this.router, result.message);
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[CodeApplicabilityDialog] Error starting analysis:', error);
      this.snackBar.open('An unexpected error occurred. Please try again.', 'Dismiss', { duration: 5000 });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
